import { getBackendUrl } from "@Utils";
import { useTranslation } from "react-i18next";
/* eslint no-loop-func: 0 */
import { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { v4 as uuid } from "uuid";
import { BUTTON_TYPES } from "../../../cubeComponent/constants/globalVariable";
import ButtonComponent from "../../../cubeComponent/form/ButtonComponent";
import { refreshFileManager, processFileList } from "../../../actions/fileUpload";

import {
  setIsFileVersionUploading,
  setFileUploadProperties,
  minimiseFileUpload,
  closeFileUpload,
  setFileUploadData,
  removeAllFileUploadData,
  updateFileUploadData,
  emptyFileUploadData,
} from "../../../reducers/minimiseFileUpload";

import axiosWrapper from "../../../services/axiosWrapper";
import minimiseIcon from "../../../assets/icons/svg/Minimise.svg";
import ModalStateDialogComponent from "../../../cubeComponent/html/modal/ModalWithState";
import FilesGrid from "./FilesSlickGrid";
import IconComponent from "../../../cubeComponent/icons";
import { showToastNotification } from "../../../actions/toast";
import { TOAST_TYPE_SUCCESS, TOAST_TYPE_ERROR, SOMETHING_WENT_WRONG } from "../../../services/constants";
import UploadFileMinimiser from "./UploadFileMinimiser";
import TextBoxComponent from "@CubeComponents/form/TextBoxComponent";
import ToolTipComponent from "../../../cubeComponent/html/tooltip";
import DropDownListComponent from "@CubeComponents/form/DropdownListComponent";
import { spacesDataService } from "../../../services/dataManager";
import { Query } from "@syncfusion/ej2-data";
import _ from "lodash";
import useFormValidator from "../../FormValidation/useFormValidator";
import useCustomFields from "@Services/utils/useCustomFields";
import ErrorBanner from "./ErrorBanner";
import ErrorModal from "./ErrorModal";

function FileUpload() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    currentFolder,
    isFileVersionUploading,
    projectData,
    fileUploadState,
    selectedFiles,
    fileUploadList,
    currentFileUploadProperties,
  } = useSelector((state) => state);
  const {
    fileAllowedExtensions = {},
    currentFiles = [],
    workspaceId,
    selectedSpaceTypeInFileManager = "this",
    draggedFiles,
    isFolderUpload = false,
    fileNomenclature,
    isUploadAllowed,
    formTemplate = { template: [] },
    fileUploadStateRef,
    currentFolderRef = {},
    hierarchyId = "0",
  } = currentFileUploadProperties || {};
  const [fileUploaded, setFileUploaded] = useState(false);
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [allBatchIds, setAllBatchIds] = useState([]);
  const [enableDone, setEnableDone] = useState(false);
  const [filesList, setFilesList] = useState([]);
  const [openFileUploadCancelModal, setOpenFileUploadCancelModal] = useState(false);
  const [uploadFolderData, setUploadFolderData] = useState({});
  const [inputKey, setInputKey] = useState(0);
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const formValidator = useFormValidator({});
  const [showCustomFieldBanner, setShowCustomFieldBanner] = useState(true);

  const query = new Query().addParams("projectId", projectData._id);
  const [uploadSpaceId, setUploadSpaceId] = useState(workspaceId);
  const { validateFormWithId } = useCustomFields();

  const isUploadFolderMismatch =
    currentFolder?.hierarchyId &&
    currentFileUploadProperties?.hierarchyId &&
    currentFolder.hierarchyId !== currentFileUploadProperties.hierarchyId &&
    fileUploadState === "MAXIMISED";

  useEffect(() => {
    if (!uploadFolderData.id && currentFolderRef) {
      setUploadFolderData({
        id: currentFolderRef.hierarchyId,
        name: currentFolderRef.name,
        path: `${currentFolderRef.filterPath}${currentFolderRef.name}`,
      });
    } else if (isFileVersionUploading) {
      setUploadFolderData({
        id: hierarchyId,
        name: `/`,
        path: `/`,
      });
    }
  }, []);

  useEffect(() => {
    if (!workspaceId && uploadSpaceId) {
      dispatch(
        setFileUploadProperties({
          [`${uploadSpaceId}_${hierarchyId}`]: {
            workspaceId: uploadSpaceId,
            formTemplate,
          },
        }),
      );
    }
  }, [uploadSpaceId]);

  useEffect(() => {
    if (draggedFiles && !isFolderUpload) {
      handleFileInputChange(draggedFiles);
    }
  }, [draggedFiles, isFolderUpload]);

  useEffect(() => {
    if (!workspaceId && projectData.defaultSpace) {
      setUploadSpaceId(projectData.defaultSpace._id);
      setUploadFolderData({
        id: "0",
        name: "/",
        path: `/${projectData.defaultSpace.name}`,
      });
    }
  }, [workspaceId]);

  const handleDragOver = (event) => {
    event.preventDefault();
    setFileUploaded(false);
  };

  function handleDragLeave(event) {
    event.preventDefault();
  }

  useEffect(() => {
    if (fileUploadList.length) {
      if (!fileUploaded) {
        const batchId = uuid();
        setCurrentBatchId(batchId);
        if (!allBatchIds.includes(batchId)) {
          setAllBatchIds((prev) => [...prev, batchId]);
        }
        fileUploadList.forEach((file, index) => {
          if (file.progress == 0 && !file.triggerFileUpload && !file.isDeleted) {
            handleFileUpload(index, batchId);
          }
        });
        setFileUploaded(true);
      }
      const allFiles = [];
      fileUploadList.forEach((file) => {
        allFiles.push({
          fileId: file.fileId,
          progressCompleted: file.progressCompleted,
          name: file.name,
          progress: file.progress,
          isFileNomenclatureCorrect: file.isFileNomenclatureCorrect,
          isDeleted: file.isDeleted,
          isFolder: file.isFolder,
        });
      });
      if (JSON.stringify(filesList) != JSON.stringify(allFiles)) {
        setFilesList(allFiles);
      }
    }
  }, [fileUploadList]);

  const traverseFileTree = async (item, path) => {
    let output = [];
    path = path || "";
    if (item.isFile) {
      await new Promise((res) => {
        item.file((file) => {
          file.customWebkitRelativePath = path + file.name;
          output.push(file);
          res();
        });
      });
    } else if (item.isDirectory) {
      const dirReader = item.createReader();
      await new Promise((res) => {
        dirReader.readEntries(async (entries) => {
          for (let i = 0; i < entries.length; i++) {
            output = [...output, ...(await traverseFileTree(entries[i], `${path + item.name}/`))];
          }
          res();
        });
      });
    }
    return output;
  };

  const handleDrop = async (event) => {
    event.preventDefault();

    let fileList = [];
    const items = event.dataTransfer.items;
    const entries = [];
    for (const it of items) {
      if (it.webkitGetAsEntry) {
        const entry = it.webkitGetAsEntry();
        if (entry) entries.push(entry);
      }
    }

    for (const entry of entries) {
      if (entry.isDirectory) {
        const folderFiles = await traverseFileTree(entry);
        fileList.push({
          name: entry.name,
          size: folderFiles.reduce((acc, f) => acc + f.size, 0),
          resourcesList: folderFiles,
          isFolder: true,
        });
      } else if (entry.isFile) {
        const file = await new Promise((res) => entry.file(res));
        fileList.push(file);
      }
    }

    if (!fileList.length && event.dataTransfer.files?.length) {
      fileList = Array.from(event.dataTransfer.files);
    }

    fileList = processFileList({
      fileList,
      currentFiles,
      fileNomenclature,
      isFileVersionUploading,
      uploadFolderData,
      uploadSpaceId,
      selectedSpaceTypeInFileManager,
      currentFolder,
    });

    dispatch(setFileUploadData(fileList));
  };

  const abortOnClose = () => {
    fileUploadList.forEach((file) => file.controller?.forEach((item) => item.abort()));
  };

  const handleCloseFileUploadModal = async () => {
    const currentFiles = fileUploadList.filter((file) => !file.isDeleted);
    const initialFilesCount = currentFiles.length;
    let filesUploadedAsNewVersion = 0;
    currentFiles.forEach((file) => {
      if (file.isDuplicate && file.progressCompleted) {
        filesUploadedAsNewVersion += 1;
      }
    });
    abortOnClose();
    dispatch(removeAllFileUploadData());
    if (isFileVersionUploading && fileUploadList.length) {
      const fileVersionsUploadFailed = !!fileUploadList.filter((item) => !item.progressCompleted && !item.isDeleted)
        .length;
      if (fileVersionsUploadFailed) {
        dispatch(
          showToastNotification({
            content: t("Error in File(s) upload."),
            type: TOAST_TYPE_ERROR,
          }),
        );
      } else {
        dispatch(refreshFileManager(true));
        dispatch(
          showToastNotification({
            content: t("File(s) uploaded successfully."),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      }
    } else if (currentBatchId && initialFilesCount) {
      axiosWrapper
        .post(`${getBackendUrl()}/fileOperations/handleBatchNotification`, {
          batchIds: allBatchIds,
          initialFilesCount,
          isFolder: isFolderUpload,
          filesUploadedAsNewVersion,
        })
        .then((response) => {
          if (response.data.status) {
            dispatch(
              showToastNotification({
                content: response.data.message,
                type: TOAST_TYPE_SUCCESS,
              }),
            );
          } else {
            dispatch(
              showToastNotification({
                content: response.data.message,
                type: TOAST_TYPE_ERROR,
              }),
            );
          }
        });
    }
    dispatch(setIsFileVersionUploading(false));
    dispatch(closeFileUpload());
    dispatch(emptyFileUploadData());
    setOpenFileUploadCancelModal(false);
    setUploadFolderData({});

    setInputKey((k) => k + 1);
    dispatch(refreshFileManager(true));
  };

  const onCloseFileUploadModal = () => {
    if (fileUploadList.length && !enableDone) {
      setOpenFileUploadCancelModal(true);
    } else {
      handleCloseFileUploadModal();
    }
  };

  const handleDone = () => {
    const filteredFiles = fileUploadList.filter((file) => !file.isBulkEdit && !file.isDeleted);
    const allValid = validateAllFiles(filteredFiles);

    if (allValid) {
      handleCloseFileUploadModal();
      dispatch(refreshFileManager(true));
    }
  };

  const validateAllFiles = (filteredFiles) => {
    return filteredFiles.every((file, index) => {
      const templateWithAnswers = buildTemplateWithAnswers(file);
      return validateFormWithId(index + 1, templateWithAnswers, formValidator);
    });
  };

  const buildTemplateWithAnswers = (file) => {
    if (!formTemplate.template?.length) {
      return [];
    }

    return _.cloneDeep(formTemplate.template).map((section) => ({
      ...section,
      questionFields: populateQuestionFields(section.questionFields, file),
    }));
  };

  const populateQuestionFields = (questionFields, file) => {
    return questionFields.map((question) => {
      if (file[question._id]) {
        return {
          ...question,
          answer: { value: file[question._id] },
        };
      }
      return question;
    });
  };

  const handleFileInputChange = (eventOrFiles) => {
    const selectedFiles = eventOrFiles?.target?.files || eventOrFiles;
    if (_.isEmpty(selectedFiles)) {
      return;
    }
    let fileList = null;

    if (isFolderUpload) {
      const folderSize = Array.from(selectedFiles).reduce((acc, curr) => acc + curr.size, 0);
      const rootFolderName = selectedFiles[0].webkitRelativePath.split("/")[0];

      fileList = [
        {
          name: rootFolderName,
          size: folderSize,
          resourcesList: [...selectedFiles],
          isFolder: true,
        },
      ];
    } else {
      let error;
      if (!isUploadAllowed) {
        error = "Insufficient permission";
      }

      const newFileList = [...selectedFiles].map((file) => {
        if (error) {
          file.error = error;
        }
        return file;
      });

      fileList = [...newFileList];
    }

    fileList = processFileList({
      fileList,
      currentFiles,
      fileNomenclature,
      isFileVersionUploading,
      uploadFolderData,
      uploadSpaceId,
      selectedSpaceTypeInFileManager,
      currentFolder,
    });

    dispatch(setFileUploadData(fileList));
    setFileUploaded(false);
  };

  const createFolder = async ({ hierarchyId, name, spaceId }) => {
    const response = await axiosWrapper.post(`${getBackendUrl()}/fileSystemServer`, {
      action: "create",
      name,
      data: [
        {
          hierarchyId,
          name,
          spaceId,
        },
      ],
    });
    return response;
  };

  const triggerFolderUpload = async (file, fileIndex, fileMetadata, batchId) => {
    const foldersCreated = {};
    initializeUpload(fileIndex, fileMetadata);

    try {
      await processAllFiles(file.resourcesList, fileIndex, fileMetadata, foldersCreated, batchId);
      finalizeUpload(fileIndex);
    } catch (error) {
      handleUploadError(fileIndex, error);
    }
  };

  const initializeUpload = (fileIndex, fileMetadata) => {
    setUploadSpaceId(fileMetadata.spaceId);
    dispatch(
      updateFileUploadData({
        index: fileIndex,
        spaceId: uploadSpaceId,
        hierarchyId,
        data: {
          progress: 0,
          status: "Uploading...",
        },
      }),
    );
  };

  const processAllFiles = async (resourcesList, fileIndex, fileMetadata, foldersCreated, batchId) => {
    for (let i = 0; i < resourcesList.length; i++) {
      if (isUploadCanceled(fileIndex)) break;

      const currentFile = resourcesList[i];
      const folderPath = getFolderPath(currentFile);

      await processSingleFile(currentFile, folderPath, fileIndex, fileMetadata, foldersCreated, i, batchId);
    }
  };

  const getFolderPath = (file) => {
    return file.customWebkitRelativePath
      ? file.customWebkitRelativePath.split("/")
      : file.webkitRelativePath.split("/");
  };

  const processSingleFile = async (
    currentFile,
    splitWebkitpath,
    fileIndex,
    fileMetadata,
    foldersCreated,
    fileIndexInList,
    batchId,
  ) => {
    for (let j = 1; j < splitWebkitpath.length; j++) {
      if (isUploadCanceled(fileIndex)) break;

      const folderPath = splitWebkitpath.slice(0, j).join("/");
      await ensureFolderExists(folderPath, fileIndex, fileMetadata, foldersCreated);

      if (j === splitWebkitpath.length - 1) {
        await uploadFileToFolder(
          currentFile,
          folderPath,
          fileIndex,
          fileMetadata,
          foldersCreated,
          fileIndexInList,
          batchId,
        );
      }
    }
  };

  const ensureFolderExists = async (folderPath, fileIndex, fileMetadata, foldersCreated) => {
    if (Object.hasOwn(foldersCreated, folderPath)) return;

    const parentFolderPath = getParentFolderPath(folderPath);
    const folderName = getFolderName(folderPath);

    const response = await createFolder({
      hierarchyId: parentFolderPath ? foldersCreated[parentFolderPath] : fileMetadata.hierarchyId,
      name: folderName,
      spaceId: fileMetadata.spaceId,
    });

    const data = response.data;
    if (!data.status) {
      const error = new Error(data.message || "Failed to create folder");
      error.response = { ...response, data };
      throw error;
    }

    const folderId = data.files._id;
    foldersCreated[folderPath] = folderId;

    if (!parentFolderPath) {
      dispatch(
        updateFileUploadData({
          index: fileIndex,
          spaceId: uploadSpaceId,
          hierarchyId,
          data: { folderId },
        }),
      );
    }
  };

  const uploadFileToFolder = async (
    file,
    folderPath,
    fileIndex,
    fileMetadata,
    foldersCreated,
    fileIndexInList,
    batchId,
  ) => {
    const response = await uploadFile({
      fileMetadata: {
        ...fileMetadata,
        hierarchyId: foldersCreated[folderPath],
      },
      file,
      folderIndex: fileIndex,
      fileIndex: fileIndexInList,
      batchId,
    });

    if (!response.data.status) {
      throw new Error("Upload failed");
    }

    dispatch(
      updateFileUploadData({
        index: fileIndex,
        spaceId: uploadSpaceId,
        hierarchyId,
        data: {
          bytesUploaded: file.size,
          hierarchyId: foldersCreated[folderPath],
        },
      }),
    );
  };

  const getParentFolderPath = (folderPath) => {
    return folderPath.split("/").slice(0, -1).join("/");
  };

  const getFolderName = (folderPath) => {
    return folderPath.split("/").at(-1);
  };

  const isUploadCanceled = (fileIndex) => {
    return _.get(fileUploadList, `[${fileIndex}].isDeleted`, false);
  };

  const finalizeUpload = (fileIndex) => {
    if (!isUploadPopupClosed()) {
      dispatch(
        updateFileUploadData({
          index: fileIndex,
          spaceId: uploadSpaceId,
          hierarchyId,
          data: { progressCompleted: true },
        }),
      );
    }
  };

  function isUploadPopupClosed() {
    return fileUploadStateRef && fileUploadStateRef.current && fileUploadStateRef.current == "CLOSED";
  }

  const uploadFile = async ({
    fileMetadata = {},
    file,
    fileIndex,
    folderIndex,
    onUploadProgress = () => {},
    batchId,
  }) => {
    setUploadSpaceId(fileMetadata.spaceId);
    const formData = new FormData();
    const data = {
      hierarchyId: fileMetadata.hierarchyId,
      spaceId: fileMetadata.spaceId,
      spaceType: fileMetadata.spaceType,
      location: fileMetadata.location,
      documentType: fileMetadata.documentType,
      Title_mp: fileMetadata.Title_mp,
      Location_mp: fileMetadata.Location_mp,
      Remarks_mp: fileMetadata.Remarks_mp,
      batchId,
    };

    formData.append("uploadFiles", file);
    formData.append("action", "save");
    formData.append("getFileId", true);
    formData.append("data", JSON.stringify(data));

    const controller = new AbortController();
    const signal = controller.signal;

    let upload = true;
    if (fileMetadata.isFolder) {
      upload = fileMetadata.isFolder && !fileUploadList[folderIndex].isDeleted;
    } else {
      upload = !fileUploadList[fileIndex].isDeleted;
    }

    const index = fileMetadata.isFolder ? folderIndex : fileIndex;

    if (isUploadPopupClosed()) {
      upload = false;
    } else {
      const controllerList = [controller];
      if (fileMetadata.isFolder) {
        controllerList[fileIndex] = controller;
      }
      dispatch(
        updateFileUploadData({
          index,
          data: {
            progressStarted: true,
            controller: controllerList,
          },
        }),
      );
    }

    if (upload) {
      return await axiosWrapper.post(`${getBackendUrl()}/fileSystemServer/Upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (percentageComplete, totalBytesDone) =>
          onUploadProgress(index, percentageComplete, totalBytesDone),
        signal,
      });
    }
    return false;
  };

  const handleUploadError = (fileIndex, err) => {
    const errorMessage = getErrorMessage(err);
    dispatch(
      updateFileUploadData({
        index: fileIndex,
        spaceId: uploadSpaceId,
        hierarchyId,
        data: {
          error: errorMessage,
        },
      }),
    );
    if (document.getElementById(`error_text_${fileIndex + 1}`)) {
      document.getElementById(`error_text_${fileIndex + 1}`).innerText = errorMessage;
    }
  };

  const handleReadMoreClick = () => {
    setShowErrorModal(true);
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
  };

  const handleDismissErrorBanner = () => {
    setShowErrorBanner(false);
  };

  const getErrorMessage = (err) => {
    let errorMessage = _.get(err, "response.data.errorMessage");
    if (!errorMessage && _.get(err, "code") == "ERR_NETWORK") {
      setShowErrorBanner(true);
    } else if (!errorMessage) {
      errorMessage = SOMETHING_WENT_WRONG;
    }
    return errorMessage;
  };

  const triggerFileUpload = async (file, fileIndex, fileMetadata, batchId) => {
    try {
      const response = await uploadFile({
        fileMetadata,
        file,
        fileIndex,
        onUploadProgress: uploadProgressCallback,
        batchId,
      });
      if (response.data.status) {
        dispatch(
          updateFileUploadData({
            index: fileIndex,
            spaceId: uploadSpaceId,
            hierarchyId,
            data: {
              progressCompleted: true,
              fileId: response.data.fileId,
            },
          }),
        );
      } else {
        handleUploadError(fileIndex, { response });
      }
    } catch (err) {
      getErrorMessage(err);
    }
  };

  const uploadProgressCallback = (fileIndex, percentageComplete) => {
    const uploadClosed = isUploadPopupClosed();
    if (fileUploadList.length && fileUploadList[fileIndex] && !uploadClosed) {
      dispatch(
        updateFileUploadData({
          index: fileIndex,
          spaceId: uploadSpaceId,
          hierarchyId,
          data: {
            progress: percentageComplete,
            status: "Uploading...",
          },
        }),
      );
    }
  };

  const handleMinimiseModal = (event) => {
    dispatch(minimiseFileUpload());
    event.stopPropagation();
  };

  const handleBrowseFileButtonClick = () => {
    const input = document.getElementById("file-upload-input");
    if (input) {
      input.click();
      input.value = "";
    }
  };

  const handleFileUploadAsNewFile = async (fileIndex, batchId) => {
    let { file } = fileUploadList[fileIndex];
    const { isFolder } = fileUploadList[fileIndex];
    const fileType = file.type;
    const { name } = file;

    if (!isFolder) {
      file = new File([file], name, {
        type: fileType,
      });
    }

    dispatch(
      updateFileUploadData({
        index: fileIndex,
        spaceId: uploadSpaceId,
        hierarchyId,
        data: {
          progress: 0,
          progressCompleted: false,
          triggerFileUpload: true,
        },
      }),
    );

    if (!isFolder) {
      await triggerFileUpload(file, fileIndex, fileUploadList[fileIndex], batchId);
    } else {
      await triggerFolderUpload(file, fileIndex, fileUploadList[fileIndex], batchId);
    }
  };

  const handleFileUploadAsNewVersion = async (fileIndex) => {
    try {
      const fileMetadata = fileUploadList[fileIndex];
      const fileUploaded = fileMetadata.file;
      let hierarchyId = "";

      dispatch(
        updateFileUploadData({
          index: fileIndex,
          spaceId: uploadSpaceId,
          hierarchyId,
          data: {
            duplicateFileStatus: { found: false },
            progress: 0,
            progressCompleted: false,
            triggerFileUpload: true,
          },
        }),
      );

      if (isFileVersionUploading === true) {
        hierarchyId = selectedFiles[0].hierarchyId;
      } else {
        const allFiles = currentFiles;
        allFiles.forEach((file) => {
          if (file.name == fileUploaded.name) {
            hierarchyId = file.hierarchyId;
          }
        });
      }

      const formData = new FormData();
      const data = {
        location: fileMetadata.location,
        documentType: fileMetadata.documentType,
      };
      formData.append("uploadFiles", fileUploaded);
      formData.append("fileId", hierarchyId);
      formData.append("name", fileUploaded.name);
      formData.append("data", JSON.stringify(data));
      formData.append("spaceId", uploadSpaceId);

      const config = {
        onUploadProgress: uploadProgressCallback.bind(this, fileIndex),
      };

      const response = await axiosWrapper.post(`${getBackendUrl()}/fileUpload/uploadNewVersion`, formData, config);
      if (response.data.status) {
        dispatch(
          updateFileUploadData({
            index: fileIndex,
            spaceId: uploadSpaceId,
            hierarchyId,
            data: {
              progressCompleted: true,
            },
          }),
        );
      } else {
        handleUploadError(fileIndex, { response });
      }
    } catch (err) {
      handleUploadError(fileIndex, err);
    }
  };

  const handleSpaceChange = (value) => {
    setUploadSpaceId(value);
    setUploadSpaceId(value);
    setUploadFolderData({
      id: "0",
      name: "/",
      path: "/",
    });
  };

  const handleFileUpload = (fileIndex, batchId) => {
    if (isFileVersionUploading === true) {
      handleFileUploadAsNewVersion(fileIndex);
    } else {
      handleFileUploadAsNewFile(fileIndex, batchId);
    }
  };

  const filesModalContent = () => {
    const folderUploadProps = isFolderUpload ? { directory: "", webkitdirectory: "", mozdirectory: "" } : {};

    const fileOrFolderText = isFolderUpload ? "folder(s)" : "file(s)";

    return (
      <div className="h-full overflow-none">
        {showErrorBanner && <ErrorBanner onDismiss={handleDismissErrorBanner} onReadMore={handleReadMoreClick} />}
        {filesList.length ? (
          <div className="flex flex-col h-full">
            <div>
              <section
                className="flex border items-center ps-3 py-4 border-solid border-[color:var(--soft_gray)]"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                aria-label="Drag and drop area or browse to upload"
              >
                <input
                  id="file-upload-input"
                  key={inputKey}
                  accept={fileAllowedExtensions.allowedExtensions}
                  multiple={isFolderUpload || isFileVersionUploading !== true}
                  onChange={handleFileInputChange}
                  type="file"
                  hidden
                  {...folderUploadProps}
                />
                <ButtonComponent
                  onClick={handleBrowseFileButtonClick}
                  className="e-flat"
                  disabled={!uploadSpaceId}
                  aria-label="Browse for files"
                >
                  Browse...
                </ButtonComponent>
                <div style={{ fontSize: "14px", paddingInlineStart: "10px" }}>{`Or drop ${fileOrFolderText} here`}</div>
              </section>
              {showCustomFieldBanner && formTemplate.template?.length && isFolderUpload ? (
                <div className="flex items-center justify-between gap-2 text-sm mt-4 bg-[#FFCF55]/40 w-full p-2 rounded">
                  <p className="flex-1">
                    {t("Custom fields value set for a folder will be applied to all the files within that folder.")}
                  </p>
                  <button
                    onClick={() => setShowCustomFieldBanner(false)}
                    className="text-gray-600 hover:text-black"
                    aria-label="Dismiss banner"
                  >
                    ✕
                  </button>
                </div>
              ) : null}
            </div>

            {filesList.length ? (
              <section
                className="flex-1 border-t border-[color:var(--soft_gray)] overflow-auto"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                aria-label="Uploaded files area"
              >
                <input
                  id="file-upload-input"
                  key={inputKey}
                  accept={fileAllowedExtensions.allowedExtensions}
                  multiple={isFolderUpload || isFileVersionUploading !== true}
                  onChange={handleFileInputChange}
                  type="file"
                  hidden
                  {...folderUploadProps}
                />
                <FilesGrid
                  formValidator={formValidator}
                  template={formTemplate.template}
                  formTemplateId={formTemplate.formTemplateId}
                  workspaceId={uploadSpaceId}
                  setEnableDone={setEnableDone}
                  fileNomenclature={fileNomenclature}
                  isFolderUpload={isFolderUpload}
                />
              </section>
            ) : null}
          </div>
        ) : (
          <div className="h-full">
            <section
              className="flex border items-center justify-center ps-3 py-4 border-solid border-[color:var(--soft_gray)] h-full w-full"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              aria-label="No files uploaded area"
            >
              <input
                id="file-upload-input"
                key={inputKey}
                accept={fileAllowedExtensions.allowedExtensions}
                multiple={isFolderUpload || isFileVersionUploading !== true}
                onChange={handleFileInputChange}
                type="file"
                hidden
                {...folderUploadProps}
              />
              <div className="flex flex-col justify-center items-center">
                <IconComponent name="NoData" className="h-40" />
                <div className="text-lg my-2 text-[color:var(--text-gray)]">{`No ${
                  isFolderUpload ? "Folder" : "Files"
                } Uploaded`}</div>
                <div className="flex text-sm mb-2">
                  <span className="font-semibold">Upload&nbsp;</span>
                  <div>{`${fileOrFolderText} or drag in the boundary box to get started`}</div>
                </div>
                <ButtonComponent
                  onClick={handleBrowseFileButtonClick}
                  className="e-flat"
                  disabled={!uploadSpaceId}
                  aria-label="Browse for files"
                >
                  Browse...
                </ButtonComponent>
              </div>
            </section>
          </div>
        )}
      </div>
    );
  };

  const getSpacesDropdown = useCallback(
    () => (
      <DropDownListComponent
        dataSource={spacesDataService}
        query={query}
        fields={{ text: "spaceName", value: "_id" }}
        allowFiltering
        onChange={(event) => handleSpaceChange(event.target.value)}
        width="auto"
        value={uploadSpaceId}
        popupWidth="200px"
        enabled={!workspaceId && !fileUploadList.length}
      />
    ),
    [uploadSpaceId, fileUploadList],
  );

  const headerContent = () => (
    <div className="flex w-full justify-between" data-testid="headerContent">
      <span className="text-2xl text-[color:var(--primary-text)] font-semibold" data-testid="uploadFiles">
        {isFolderUpload ? t("Upload Folder") : t("Upload Files")}
        {uploadFolderData?.id != 0 ? ` | ${uploadFolderData.name}` : ""}
      </span>
      <div className="flex items-center gap-4">
        {!workspaceId && getSpacesDropdown()}
        <ToolTipComponent
          showTipPointer={false}
          position="bottom"
          content={
            uploadFolderData.name
              ? uploadFolderData.path || "/"
              : "This project has no default space. Please choose a space."
          }
        >
          <TextBoxComponent
            value={uploadFolderData.name ? uploadFolderData.name : "Please select space"}
            disabled
            className=""
          />
        </ToolTipComponent>
        <img src={minimiseIcon} className="w-5 h-5 cursor-pointer" onClick={handleMinimiseModal} />
      </div>
    </div>
  );

  const footerContent = () =>
    fileUploadList.length ? (
      <div className="h-auto mt-4">
        <ButtonComponent
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          onClick={handleDone}
          data-testid="nextAddUserBtn"
          disabled={!enableDone}
        >
          Done
        </ButtonComponent>
      </div>
    ) : null;

  const uploadModal = () => (
    <ModalStateDialogComponent
      id="file-upload-modal"
      isVisible
      width="90%"
      onCancel={handleCloseFileUploadModal}
      zIndex={400}
      headerTitle={headerContent()}
      modelContent={filesModalContent()}
      footerContent={footerContent()}
      height="95%"
    />
  );

  const onClickFileUploadCancelModalClose = () => {
    handleCloseFileUploadModal();
  };

  const handleFileUploadCancelModalClose = () => {
    setOpenFileUploadCancelModal(false);
  };

  const uploadFileMinimiser = useCallback(
    () => (
      <UploadFileMinimiser
        handleCloseFileUploadModal={onCloseFileUploadModal}
        showErrorBanner={showErrorBanner}
        onDismiss={handleDismissErrorBanner}
        onReadMore={handleReadMoreClick}
      />
    ),
    [fileUploadList, onCloseFileUploadModal, showErrorBanner, handleDismissErrorBanner, handleReadMoreClick],
  );
  return (
    <div>
      {fileUploadState === "MAXIMISED" && (
        <div className={isUploadFolderMismatch ? "pointer-events-none opacity-50" : ""}>{uploadModal()}</div>
      )}

      {uploadFileMinimiser()}
      {showErrorModal && <ErrorModal isVisible={showErrorModal} onClose={handleCloseErrorModal} />}
      {openFileUploadCancelModal && (
        <ModalStateDialogComponent
          isVisible
          onCancel={handleFileUploadCancelModalClose}
          footerContent={
            <div className="flex w-full gap-4">
              <ButtonComponent onClick={onClickFileUploadCancelModalClose} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
                Yes
              </ButtonComponent>
              <ButtonComponent onClick={handleFileUploadCancelModalClose} buttonType={BUTTON_TYPES.BORDER_BUTTON}>
                No
              </ButtonComponent>
            </div>
          }
          headerTitle={
            <div className="flex justify-between w-full items-center">
              <div>{t("Cancel Upload")}</div>
            </div>
          }
          modelContent={
            <div className="w-full">
              <div className="my-4">
                Files in uploading state will be cancelled. Are you sure you want to cancel file upload?
              </div>
            </div>
          }
          width="40%"
          allowModalPadding
          zIndex={500}
        />
      )}
    </div>
  );
}
export default FileUpload;
